/**
 * What a `PlannedWorkPeriodPreset` MEANS as dates — the one arithmetic both sides run.
 *
 * The preset vocabulary is tenant policy and already lives in this package, but until now only the
 * web could turn a preset into a range: the resolution lived in the board's filter block. That left
 * the server unable to honour its own setting, so a request that arrived without a window could only
 * be refused. A refusal is the wrong answer to "the viewer has not chosen yet" — the tenant HAS
 * stated where a planner should arrive, and the server holds that statement.
 *
 * DATES, not instants, and deliberately zone-free. A period is a claim about the calendar — October
 * is October wherever you read it from — and planned work is identified by plan, line and LOCAL date.
 * Converting a window to UTC instants to resolve it is what produces off-by-one days at the ends, so
 * the range is derived from a date string and stays a pair of date strings. Any conversion to instants
 * is a reader's own business and belongs at the last possible point, against the occurrence's own
 * timezone rather than whoever asked the question.
 *
 * A week-family period starts on a MONDAY, so a fortnight chosen on a Sunday covers the same two weeks
 * it would have on the Monday before — the boundary must not move with the day the viewer opens the
 * board.
 */

import { DateTime } from 'luxon';
import { PlannedWorkPeriodPreset } from './planned.work.settings';

/** An inclusive range of local calendar dates (`yyyy-MM-dd`). */
export interface PlannedWorkPeriodRange {
    from: string;
    to: string;
}

/** Weeks each week-family preset spans. Month presets are handled on their own path. */
const PRESET_WEEKS: Readonly<Partial<Record<PlannedWorkPeriodPreset, number>>> = {
    [PlannedWorkPeriodPreset.ThisWeek]: 1,
    [PlannedWorkPeriodPreset.NextWeek]: 1,
    [PlannedWorkPeriodPreset.ThisFortnight]: 2,
    [PlannedWorkPeriodPreset.NextFortnight]: 2,
};

/**
 * How far the period's START moves from the period containing the anchor.
 *
 * Months move in months; week-family presets move in WEEKS. `Next fortnight` starts ONE week on —
 * the two weeks that FOLLOW this one — so it must not skip the week between.
 */
const PRESET_OFFSET: Readonly<Record<PlannedWorkPeriodPreset, number>> = {
    [PlannedWorkPeriodPreset.ThisWeek]: 0,
    [PlannedWorkPeriodPreset.NextWeek]: 1,
    [PlannedWorkPeriodPreset.ThisFortnight]: 0,
    [PlannedWorkPeriodPreset.NextFortnight]: 1,
    [PlannedWorkPeriodPreset.ThisMonth]: 0,
    [PlannedWorkPeriodPreset.NextMonth]: 1,
};

/**
 * Today as a calendar date, in `timezone` when one is given.
 *
 * The ONE place a zone is legitimately involved: "what day is it" is the only question about a period
 * whose answer depends on where you stand. Everything downstream works on the date this returns.
 */
export function plannedWorkToday(timezone?: string | null): string {

    return DateTime.local({ zone: timezone ?? 'local' }).toISODate()!;
}

/**
 * The inclusive date range a preset names, around `anchor` (a `yyyy-MM-dd` date, normally today).
 *
 * Zone-free by construction: the anchor is parsed as a bare calendar date in UTC purely so the
 * arithmetic has a stable footing, and only date strings come back out. No instant it computes ever
 * escapes, so no zone can shift the ends by a day.
 *
 * An unusable anchor collapses to a single day rather than throwing: this resolves a DEFAULT, and a
 * default that can fail is a default the caller still has to guard.
 */
export function plannedWorkPeriodRange(preset: PlannedWorkPeriodPreset, anchor: string): PlannedWorkPeriodRange {

    const at = DateTime.fromISO(anchor, { zone: 'utc' });
    if (!at.isValid) return { from: anchor, to: anchor };

    const offset = PRESET_OFFSET[preset];
    const weeks = PRESET_WEEKS[preset];

    if (!weeks) {
        const start = at.startOf('month').plus({ months: offset });
        return { from: start.toISODate()!, to: start.endOf('month').toISODate()! };
    }

    const start = at.startOf('week').plus({ weeks: offset });
    return { from: start.toISODate()!, to: start.plus({ weeks }).minus({ days: 1 }).toISODate()! };
}
